import React, {useState} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {Stack, Box, StackProps, useMultiStyleConfig, HStack, TextProps, Text, Link} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useForm} from 'react-hook-form'
import {Button, ButtonProps, AlertMessage} from '_uiComponents'
import {AuthHelpers, useAuthHelper, useCustomerType, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {useLoginFlow, useWishlist, useOpenDrawer, useBasket} from '_customHooks'
import {useLoader} from '_context'
import {RemoveScroll} from 'react-remove-scroll'
import {FormFactory, FormValues} from '_factories'
import {getAbsoluteUrl} from '_utils'
import type {LoginError, LoginParams} from '_types'

interface LoginDrawerProps {
    phonelogin?: boolean
    hostlink?: string
}

/**
 * @name LoginDrawer
 *
 * @component
 *
 * @description
 * Renders content of Login Drawer.
 *
 * @param {LoginDrawerProps} props Properties object.
 * @param {boolean} props.phonelogin
 * @param {string} props.hostlink
 *
 * @returns {JSX.Element} Rendered `LoginDrawer` component.
 */
const LoginDrawer = ({phonelogin, hostlink}: LoginDrawerProps): JSX.Element => {
    const styles = useMultiStyleConfig('LoginDrawer') as Record<string, StackProps & TextProps>
    const {openResetPasswordDrawer} = useOpenDrawer()
    const {showLoader, hideLoader} = useLoader()
    const {formatMessage} = useIntl()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const {locale, site} = useMultiSite()
    const {executeLoginFlow} = useLoginFlow()
    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const {customerType} = useCustomerType()
    const prevAuthType = usePrevious(customerType)
    const {basket} = useBasket()
    const {publicWishlistId} = useWishlist()

    const hostLinkLocalized = JSON.parse(hostlink || '{}')[site.id]
    const registerUrl = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/registration')

    const [onLoginError, setOnLoginError] = useState(false)

    const openResetPassword = () => {
        openResetPasswordDrawer({
            phonelogin: phonelogin,
            registerUrl: registerUrl,
        })
    }

    const {
        control,
        formState: {errors},
        handleSubmit,
    } = useForm<FormValues>()

    const validateEmail = (email) => {
        const regex = /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/
        return regex.test(email)
    }

    const handleMergeBasket = () => {
        const hasBasketItem = basket.productItems?.length > 0
        const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
        if (shouldMergeBasket) {
            try {
                mergeBasket.mutate({
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    parameters: {
                        createDestinationBasket: true,
                    },
                })
            } catch (e) {
                console.error('Error merging basket:', e)
            }
        }
    }

    const loginForm: Array<{
        type: string
        name: keyof FormValues
        label: string
        placeholder?: string
        options?: {value: string; label: string}[]
        defaultValue?: string
    }> = [
        phonelogin
            ? {
                  type: 'text',
                  name: 'email-phone',
                  label: formatMessage({id: 'label.input.login.emailandphone', defaultMessage: 'Enter an e-mail or a phone number'}),
              }
            : {
                  type: 'email',
                  name: 'email',
                  label: formatMessage({id: 'label.input.login.email', defaultMessage: 'Enter e-mail'}),
              },
        {
            type: 'password',
            name: 'password',
            label: formatMessage({id: 'label.input.login.password', defaultMessage: 'Enter your password'}),
        },
    ]

    const submitLogin = async (data: FormValues) => {
        showLoader()
        if (phonelogin) {
            if (validateEmail(data['email-phone'])) {
                try {
                    const loginParams: LoginParams = {
                        username: data['email-phone'],
                        password: data.password,
                    }

                    if (publicWishlistId) {
                        loginParams.options = {body: {c_unloggedWishlistID: publicWishlistId}}
                    }

                    await login.mutateAsync(loginParams)

                    handleMergeBasket()
                    window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/account')
                    hideLoader()
                } catch (error) {
                    hideLoader()
                    const err = error as LoginError
                    if (err?.response?.status === 401) {
                        setOnLoginError(true)
                    }
                }
            } else {
                const response = await executeLoginFlow({
                    phone: data['email-phone'],
                    password: data.password,
                    unloggedWishlistID: publicWishlistId ? publicWishlistId : undefined,
                })

                if (response?.access_token) {
                    window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/account')
                } else {
                    hideLoader()
                    setOnLoginError(true)
                }
            }
        } else {
            try {
                const loginParams: LoginParams = {
                    username: data.email,
                    password: data.password,
                }

                if (publicWishlistId) {
                    loginParams.options = {body: {c_unloggedWishlistID: publicWishlistId}}
                }

                await login.mutateAsync(loginParams)
                handleMergeBasket()
                window.location.href = getAbsoluteUrl(hostLinkLocalized, locale.alias, '/account')
                hideLoader()
            } catch (error) {
                hideLoader()
                const err = error as LoginError
                if (err?.response?.status === 401) {
                    setOnLoginError(true)
                }
            }
        }
    }

    return (
        <RemoveScroll enabled>
            <Stack data-testid="login-drawer" {...styles.loginBox} as="form" noValidate onSubmit={handleSubmit((data) => submitLogin(data))}>
                {onLoginError && (
                    <AlertMessage>
                        <FormattedMessage id="error.message.login.form" defaultMessage="Invalid user name or password. Please try again." />
                    </AlertMessage>
                )}

                {loginForm.map((schema, index) => (
                    <FormFactory
                        key={`${index}_${schema.name}`}
                        control={control}
                        errors={errors}
                        name={schema.name}
                        type={schema.type}
                        label={schema.label}
                        placeholder={schema.placeholder}
                        options={schema.options}
                        defaultValue={schema.defaultValue}
                    />
                ))}

                <HStack {...styles.resetPasswordBox}>
                    <Box {...styles.resetPasswordText}>
                        <Button
                            variant="textLink"
                            type="button"
                            {...(styles.resetPasswordBtn as ButtonProps)}
                            size="xs"
                            width="xs"
                            onClick={() => openResetPassword()}
                            data-testid="reset-password-button"
                        >
                            <FormattedMessage id={'link.login.forgotpassword'} defaultMessage="Forgotten password?" />
                        </Button>
                    </Box>
                </HStack>

                <HStack>
                    <Button variant="primary" type="submit" data-testid="login-button">
                        <FormattedMessage id={'button.text.loginform'} defaultMessage="LOGIN" />
                    </Button>
                </HStack>
            </Stack>

            <Stack {...styles.registrationBox}>
                <HStack>
                    <Text {...styles.registrationTitle} data-testid="registration-title">
                        <FormattedMessage id="registration.offcanvas.title" defaultMessage="Create an account" />
                    </Text>
                </HStack>

                <HStack>
                    <Text {...styles.registrationText}>
                        <FormattedMessage
                            id="registration.offcanvas.text"
                            defaultMessage="Enjoy a personalized experience and discover all the exclusive services."
                        />
                    </Text>
                </HStack>

                <HStack>
                    <Button variant="primary" as={Link} href={registerUrl} data-testid="registration-button">
                        <FormattedMessage id="offCanvas.registration.button" defaultMessage="REGISTER" />
                    </Button>
                </HStack>
            </Stack>
        </RemoveScroll>
    )
}

export {LoginDrawer}
